import Logo from '@/assets/images/Logo.svg';
import { CustomDialog } from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { GET_ALL_NOTIFICATION_COUNT } from '@/Graphql/queries';
import useLogout from '@/hooks/useLogout';
import { cn } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useQuery } from '@apollo/client';
import { ExitIcon } from '@radix-ui/react-icons';
import { Bell, Menu, User } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Notifications from '../Notifications/Notifications';

interface MenuItemType {
  name: string;
  link: string;
  external: boolean;
}

const SiteUrl = import.meta.env.VITE_SITE_URL;

export default function PrivateHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { userDetails } = authState();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const { logout } = useLogout();

  const { data: unReadCount } = useQuery(GET_ALL_NOTIFICATION_COUNT, {
    variables: {
      filter: {
        userId: userDetails?._id,
        isRead: false,
      },
    },
    skip: !userDetails?._id,
  });

  const menuItems: Array<MenuItemType> = [
    ...(userDetails?.role === Role.Trainee
      ? [{ name: 'Home', link: getRoutes.home, external: false }]
      : []),
    { name: 'Sessions', link: getRoutes.Sessions, external: false },
    ...(userDetails?.role === Role.Trainee
      ? [{ name: 'Subscription', link: getRoutes.Subscription, external: false }]
      : []),
    ...(userDetails?.role !== Role.Trainee
      ? [{ name: 'Payments', link: getRoutes.Payment, external: false }]
      : []),
    // { name: 'Features', link: '#features', external: true },
    { name: 'FAQ', link: 'faq', external: true },
    { name: 'Privacy Policy', link: 'privacy-policy', external: true },
    { name: 'Terms and Conditions', link: 'terms-and-conditions', external: true },
    { name: 'Contact Us', link: `${SiteUrl}contact-us`, external: true },

  ];

  const MenuLinks = () => (
    <>
      {menuItems.map((item) => {
        if (item.external) {
          return (
            <button
              key={item.name}
              onClick={() => window.open(SiteUrl + item.link, '_self')}
              className='hover:text-primary text-sm cursor-pointer bg-transparent border-none'
            >
              {item.name}
            </button>
          );
        } else {
          return (
            <Link
              key={item.name}
              to={item.link}
              className={cn(
                'font-normal text-sm transition-colors hover:text-primary',
                location.pathname === item.link || location.pathname.startsWith(`${item.link}/`)
                  ? 'text-primary font-semibold'
                  : 'text-foreground',
              )}
            >
              {item.name}
            </Link>
          );
        }
      })}
    </>
  );

  return (
    <header className='flex items-center justify-between w-full px-5 py-8 md:pt-8 md:pb-8 md:px-20 sticky top-0 z-50 bg-[#181A32]'>
      <div className='flex flex-row flex-grow items-center gap-2'>
        <img
          src={Logo}
          onClick={() => window.open(SiteUrl, '_self')}
          alt='fitstatic logo'
          width={149}
          height={26}
          className='cursor-pointer'
        />
      </div>
      <nav className='flex gap-8 items-center'>
        <div className='hidden xl:flex gap-8'>
          <MenuLinks />
        </div>
        <Button
          id='notifications-btn'
          variant='ghost'
          size='icon'
          className='relative rounded-full'
          onClick={() => setIsNotificationOpen((open) => !open)}
        >
          <Bell className='h-6 w-6' />
          {unReadCount && unReadCount?.getAllNotificationCount > 0 && (
            <span className='absolute top-0 right-0 h-5 w-5 rounded-full bg-red text-[10px] font-medium text-white flex items-center justify-center'>
              {unReadCount.getAllNotificationCount > 9 ? '9+' : unReadCount.getAllNotificationCount}
            </span>
          )}
        </Button>
        <div className='hidden xl:flex'>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Avatar className={'h-10 w-10'}>
                <AvatarImage
                  src={userDetails?.profileImage || ''}
                  alt={userDetails?.firstName || ''}
                  className='object-cover'
                />
                <AvatarFallback className='bg-[#41219C] text-white'>
                  {userDetails?.firstName?.[0]}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='min-w-32'>
              <DropdownMenuItem onClick={() => navigate(getRoutes.Profile)}>
                <User className='mr-2 h-4 w-4' />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpen(true)}>
                <ExitIcon className='mr-2 h-4 w-4' />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>
      <div className='flex items-center xl:hidden'>
        <Sheet>
          <SheetTrigger asChild>
            <Button id='menu-icon-btn' variant='ghost' size='icon' aria-label='Open menu'>
              <Menu className='h-6 w-6' />
            </Button>
          </SheetTrigger>
          <SheetContent side='right' className='w-[200px] sm:w-[300px]'>
            <nav className='flex flex-col gap-4 mt-8'>
              <MenuLinks />
            </nav>
          </SheetContent>
        </Sheet>
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Avatar className={'h-10 w-10'}>
              <AvatarImage
                src={userDetails?.profileImage || ''}
                alt={userDetails?.firstName || ''}
                className='object-cover'
              />
              <AvatarFallback className='bg-[#41219C] text-white'>
                {userDetails?.firstName?.[0]}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='min-w-32'>
            <DropdownMenuItem onClick={() => navigate(getRoutes.Profile)}>
              <User className='mr-2 h-4 w-4' />
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setOpen(true)}>
              <ExitIcon className='mr-2 h-4 w-4' />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {isNotificationOpen ? (
        <Notifications
          isNotificationOpen={isNotificationOpen}
          setIsNotificationOpen={setIsNotificationOpen}
        />
      ) : null}
      <CustomDialog
        open={open}
        desc='Logging out ensures the security of your account and personal information.'
        onCancel={() => setOpen(false)}
        title='Are you sure you want to Logout?'
        onConfirm={logout}
      />
    </header>
  );
}
